import {
  $hasUpdateTag,
  TextNode,
  type EditorConfig,
  type LexicalEditor,
  type LexicalNode,
  type NodeKey,
  type SerializedTextNode,
  type Spread,
} from 'lexical';

import type { NoteEditorTextTone } from '../../config';

import { markBlockDom, markToneDom } from './blockDom';
import { NOTE_STRUCTURE_TAG } from './noteUpdateTags';

export type WordingRun = {
  // The run's place in its wording: a wording with marked runs is one node
  // per run.
  segment: number;
  marked: boolean;
  tone: NoteEditorTextTone;
  spaceBefore: boolean;
};

type SerializedWordingNode = Spread<
  { blockId: string } & WordingRun,
  SerializedTextNode
>;

const markRunDom = (dom: HTMLElement, blockId: string, run: WordingRun) => {
  markBlockDom(dom, blockId, run.spaceBefore);
  markToneDom(dom, run.tone);
  dom.toggleAttribute('data-marked', run.marked);
  return dom;
};

// The fixed wording of a text block, edited as text of the document. Its own
// type keeps Lexical from merging it with the wording of the next block.
export class WordingNode extends TextNode {
  __blockId: string;
  __run: WordingRun;

  static getType(): string {
    return 'note-wording';
  }

  static clone(node: WordingNode): WordingNode {
    return new WordingNode(node.__blockId, node.__run, node.__text, node.__key);
  }

  static importJSON(json: SerializedWordingNode): WordingNode {
    const { blockId, text, segment, marked, tone, spaceBefore } = json;
    return $createWordingNode({
      id: blockId,
      text,
      run: { segment, marked, tone, spaceBefore },
    }).updateFromJSON(json);
  }

  constructor(
    blockId = '',
    run: WordingRun = {
      segment: 0,
      marked: false,
      tone: null,
      spaceBefore: false,
    },
    text = '',
    key?: NodeKey,
  ) {
    super(text, key);
    this.__blockId = blockId;
    this.__run = run;
  }

  exportJSON(): SerializedWordingNode {
    return {
      ...super.exportJSON(),
      blockId: this.getBlockId(),
      ...this.getRun(),
    };
  }

  createDOM(config: EditorConfig, editor?: LexicalEditor): HTMLElement {
    const dom = markRunDom(
      super.createDOM(config, editor),
      this.__blockId,
      this.__run,
    );
    dom.dataset.noteWording = '';
    return dom;
  }

  updateDOM(previous: this, dom: HTMLElement, config: EditorConfig): boolean {
    const replace = super.updateDOM(previous, dom, config);
    if (
      !replace &&
      (previous.__run !== this.__run || previous.__blockId !== this.__blockId)
    ) {
      markRunDom(dom, this.__blockId, this.__run);
    }
    return replace;
  }

  // Emptied by typing, a wording stays in its line with no text, so what is
  // typed next is its text again; only the note removes it.
  remove(preserveEmptyParent?: boolean): void {
    if (this.getTextContent() || $hasUpdateTag(NOTE_STRUCTURE_TAG)) {
      super.remove(preserveEmptyParent);
    }
  }

  getBlockId(): string {
    return this.getLatest().__blockId;
  }

  // A line Enter added takes the ids the note gives its text block.
  setBlockId(blockId: string): this {
    const writable = this.getWritable();
    writable.__blockId = blockId;
    return writable;
  }

  getRun(): WordingRun {
    return this.getLatest().__run;
  }

  setRun(run: WordingRun): this {
    const writable = this.getWritable();
    writable.__run = run;
    return writable;
  }
}

export const $createWordingNode = ({
  id,
  text,
  run,
}: {
  id: string;
  text: string;
  run: WordingRun;
}) => new WordingNode(id, run, text);

export const $isWordingNode = (
  node: LexicalNode | null | undefined,
): node is WordingNode => node instanceof WordingNode;
